import { isObjectRecord } from "../../typeGuards/isObjectRecord";
import type { ArsListingBody } from "../interface";
import { isArsListingColumn } from "./isArsListingColumn";
import { isArsListingRow } from "./isArsListingRow";

/**
 * Validates if a value is an array of ArsListingColumn objects.
 *
 * @param value - Unknown value to validate
 * @param isT - Optional type guard for the generic type T used in the columns' displayPattern and headerText
 * @returns True when value is an array of valid ArsListingColumn objects
 */
function isColumnsArray<T = string>(
  value: unknown,
  isT: (value: unknown) => value is T,
): value is ReturnType<typeof isArsListingColumn>[] {
  if (!Array.isArray(value)) return false;

  /* istanbul ignore next Check for valid columns array structure */
  for (const item of value) {
    if (!isArsListingColumn<T>(item, isT)) return false;
  }

  return true;
}

/**
 * Validates if a value is an array of ArsListingRow objects.
 *
 * @param value - Unknown value to validate
 * @returns True when value is an array of valid ArsListingRow objects
 */
function isRowsArray(value: unknown): value is ReturnType<typeof isArsListingRow>[] {
  if (!Array.isArray(value)) return false;

  /* istanbul ignore next Check for valid rows array structure */
  for (const item of value) {
    if (!isArsListingRow(item)) return false;
  }

  return true;
}

/**
 * Type guard for ArsListing body data.
 *
 * @param obj - Unknown value to validate
 * @param isT - Optional type guard for the generic type T used in the columns' displayPattern and headerText
 * @returns True when value can be treated as ArsListingBody
 */
export const isArsListingBody = <T = string>(
  obj: unknown,
  isT: (value: unknown) => value is T = (value: unknown): value is T => typeof value === "string",
): obj is ArsListingBody<T> => {
  // Check object
  if (!isObjectRecord(obj)) return false;

  // Check required properties
  if (typeof obj.id !== "string") return false;
  if (!isColumnsArray<T>(obj.arsListingColumns, isT)) return false;
  if (!isRowsArray(obj.arsBodyRows)) return false;
  if (obj.dataSubsetId !== undefined && typeof obj.dataSubsetId !== "string") return false;

  // Everything is good
  return true;
};
